import { formatInUTC } from '@mode/shared/util-js';
import { escapeRegExp } from 'lodash';

export enum ExprDataType {
  Any = 'Any',
  Number = 'Number',
  String = 'String',
  Boolean = 'Boolean',
  DateTime = 'DateTime',
}

export interface ExprParam {
  readonly name: string;
  readonly desc: string;
  readonly type: ExprDataType;
}

export interface ExprReturns {
  readonly desc: string;
  readonly type: ExprDataType;
}

export enum ExprType {
  Function = 'Function',
  Statement = 'Statement',
}

// the commented out categories are not being used now
// however, we are planning to use them in the future
export enum ExprCategory {
  Number = 'Number',
  String = 'String',
  DateTime = 'Date & Time',
  Aggregate = 'Aggregate',
  Analytic = 'Analytic',
  Logical = 'Logical',
  TypeConversion = 'Type Conversion',
}

export interface Flag {
  label: string;
  startDate?: string;
  endDate: string;
}

export interface ExprDoc {
  readonly name: string;
  readonly sign: string;
  readonly desc: string;
  readonly examples: string[];
  readonly category: ExprCategory;
  readonly type: ExprType;
  readonly flag?: Flag;
}

export interface ExprDocs {
  [index: string]: ExprDoc;
}

export function findMatches(docs: ExprDoc[], search: string): ExprDoc[] {
  const pattern = new RegExp(escapeRegExp(search), 'i');
  return docs.filter((doc) => {
    return pattern.test(doc.name);
  });
}

export function make(): ExprDocs {
  const now = new Date();
  return {
    /// Number
    ABS: {
      name: 'ABS',
      sign: 'ABS(<number>)',
      desc: 'Calculates the absolute value number of the given number.',
      examples: ['ABS(-4) = 4', 'ABS([Elevation])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    CEILING: {
      name: 'CEILING',
      sign: 'CEILING(<number>)',
      desc: 'Rounds a number to the nearest integer of greater than or equal value.',
      examples: ['CEILING(3.14159) = 4', 'CEILING([Order Quantity])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    EXP: {
      name: 'EXP',
      sign: 'EXP(<number>)',
      desc: `
          Returns e raised to the power of the given number,
          where e is the Euler’s constant 2.718...
        `,
      examples: ['EXP(2) = e^2', 'EXP([Order Quantity])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    FLOOR: {
      name: 'FLOOR',
      sign: 'FLOOR(<number>)',
      desc: 'Rounds a number to the nearest integer of less than or equal value.',
      examples: ['FLOOR(3.14159) = 3', 'FLOOR([Order Quantity])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    LOG10: {
      name: 'LOG10',
      sign: 'LOG10(<number>)',
      desc: 'Returns the base 10 logarithm of a number.',
      examples: ['LOG10(100) = 2', 'LOG10([Order Quantity])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    LN: {
      name: 'LN',
      sign: 'LN(<number>)',
      desc: 'Returns the natural logarithm of a number, where the base is Euler’s constant e.',
      examples: ['LN(EXP(5)) = 5', 'LN([Order Quantity])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    MOD: {
      name: 'MOD',
      sign: 'MOD(<number>, <number>)',
      desc: 'Divides two numbers and returns their remainder.',
      examples: ['MOD(10, 3) = 1', 'MOD([Order Quantity], 100)'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    POWER: {
      name: 'POWER',
      sign: 'POWER(<number>, <number>)',
      desc: 'Returns the base raised to the inputted exponent power.',
      examples: ['POWER(2, 3) = 8', 'POWER([Order Quantity], [Price])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    ROUND: {
      name: 'ROUND',
      sign: 'ROUND(<number>, <number>)',
      desc: 'Returns the number rounded to the nearest specified decimal place.',
      examples: ['ROUND(3.14159, 4) = 3.1416', 'ROUND(AVG([Profit]), 2)'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    SQRT: {
      name: 'SQRT',
      sign: 'SQRT(<number>)',
      desc: 'Returns the square root of the given number.',
      examples: ['SQRT(9) = 3', 'SQRT([Order Quantity])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    TRUNC: {
      name: 'TRUNC',
      sign: 'TRUNC(<number>, <number>)',
      desc: 'Returns the number truncated to the nearest specified decimal place without rounding.',
      examples: ['TRUNC(3.14159, 4) = 3.1415', 'TRUNC(AVG([Profit]), 2)'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    ZN: {
      name: 'ZN',
      sign: 'ZN(<expression>)',
      desc: 'Returns the given expression if not NULL, otherwise returns 0.',
      examples: ['ZN(1, NULL, 1) = 1, 0, 1', 'ZN([Order Quantity])'],
      category: ExprCategory.Number,
      type: ExprType.Function,
    },

    /// String

    CONCAT: {
      name: 'CONCAT',
      sign: 'CONCAT(<string1>, <string2>, [... <stringN>])',
      desc: 'Returns a string resulting from the concatenation, or joining, of two or more string values in an end-to-end manner.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [
        "CONCAT('Hello ', 'world!') = 'Hello world!'",
        "CONCAT([Last Name], ', ', [First Name], ' ', [Middle Name])",
      ],
    },

    CONTAINS: {
      name: 'CONTAINS',
      sign: 'CONTAINS(<string>, <substring>)',
      desc: 'Returns TRUE if the substring is within the string (not case sensitive), otherwise returns FALSE.',
      examples: [
        "CONTAINS('Hello world!', 'lo w') = TRUE",
        "CONTAINS('Hello world!', 'hello') = TRUE",
        "CONTAINS([status], 'error')",
      ],
      category: ExprCategory.String,
      type: ExprType.Function,
    },

    STR: {
      name: 'STR',
      sign: 'STR(<expression>)',
      desc: 'Returns the string representation of the values associated with the given expression.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: ['STR([region_id])', 'STR([order_poster_qty])', `STR(#${formatInUTC(now, 'yyyy-MM-dd')}#)`],
    },

    LOWER: {
      name: 'LOWER',
      sign: 'LOWER(<string>)',
      desc: 'Returns string with all characters lowercased.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`LOWER('Hello World!')`, `LOWER([status])`],
    },

    UPPER: {
      name: 'UPPER',
      sign: 'UPPER(<string>)',
      desc: 'Returns string with all characters uppercased.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`UPPER('Hello World!')`, `UPPER([status])`],
    },

    TRIM: {
      name: 'TRIM',
      sign: 'TRIM(<string>)',
      desc: 'Removes any spaces from either side of the string',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`TRIM('   Hello World  ')`, `TRIM([status])`],
    },

    LTRIM: {
      name: 'LTRIM',
      sign: 'LTRIM(<string>)',
      desc: 'Removes any spaces from the left side of the string',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`LTRIM('  Hello World!')`, `LTRIM([status])`],
    },

    RTRIM: {
      name: 'RTRIM',
      sign: 'RTRIM(<string>)',
      desc: 'Removes any spaces from the right side of the string',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`RTRIM('Hello World!   ')`, `RTRIM([status])`],
    },

    REPLACE: {
      name: 'REPLACE',
      sign: 'REPLACE(<string>, <searchString>, <replaceString>)',
      desc: 'Replaces all occurrences of the search string with the replace string.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`REPLACE('hello', 'l', '-')`, `REPLACE([Address], 'Ceylon', 'Sri Lanka')`],
    },

    FIND: {
      name: 'FIND',
      sign: 'FIND(<string>, <subString>, [<start>])',
      desc:
        "Returns the index position of substring in string or 0 if the substring isn't found. " +
        'First character of the string is at position 1. Start is an optional argument ' +
        'to define from where to start the search.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`FIND('hello', 'l', 1)`, `FIND([Address], 'Unit')`],
    },

    LEFT: {
      name: 'LEFT',
      sign: 'LEFT(<string>, <count>)',
      desc: 'Extract the left most count characters',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`LEFT('hello', 2)`, `LEFT([Address], 3)`],
    },

    RIGHT: {
      name: 'RIGHT',
      sign: 'RIGHT(<string>, <count>)',
      desc: 'Extract the right most count characters',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`RIGHT('hello', 2)`, `RIGHT([Address], 3)`],
    },

    PLUCK: {
      name: 'PLUCK',
      sign: 'PLUCK(<string>, <delimiter>, <tokenIndex>)',
      desc: 'Splits the string along the separator/delimiter, returning the string at the corresponding token index.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`PLUCK('John Smith', ' ', 2)`, `PLUCK([address], '-', 2)`],
    },

    SUBSTR: {
      name: 'SUBSTR',
      sign: 'SUBSTR(<string>, <start>, [<length>])',
      desc:
        'Returns the substring beginning at start. Note that a start value of 1 refers to the first ' +
        'character of the string. If length is provided, the returned substring will include that ' +
        'number of characters at most.',
      type: ExprType.Function,
      category: ExprCategory.String,
      examples: [`SUBSTR('hello', 2, 2)`, `SUBSTR([Address], 4)`],
    },

    /// DateTime
    DATEADD: {
      name: 'DATEADD',
      sign: 'DATEADD(<datePart>, <number>, <datetime>)',
      desc: 'Adds an increment in the specified datePart to the datetime.',
      examples: ["DATEADD('MONTH', 1, #2020-01-15#) = #2020-02-15#", "DATEADD('WEEK', 2, [Order Time])"],
      category: ExprCategory.DateTime,
      type: ExprType.Function,
    },

    DATEDIFF: {
      name: 'DATEDIFF',
      sign: 'DATEDIFF(<datePart>, <datetime>, <datetime>)',
      desc: 'Returns the difference between the two datetimes, represented in the specified datePart.',
      examples: [
        "DATEDIFF('MONTH', #2020-02-15#, #2020-08-19#) = 6",
        "DATEDIFF('MONTH', #2020-08-19#, #2020-02-15#) = -6",
        "DATEDIFF('DAY', [Order Purchase Time], [Order Return Time])",
      ],
      category: ExprCategory.DateTime,
      type: ExprType.Function,
    },

    DATEPART: {
      name: 'DATEPART',
      sign: 'DATEPART(<datePart>, <datetime>, [<startOfWeek>, <startOfYear>])',
      desc:
        'Returns the specified part of the given datetime expression as a number. ' +
        'The returned number may change based on the day specified as start of the week or start of year. ' +
        'If not specified, the default for start of week is Sunday and the default start of year is January. ' +
        'Start of year option only applies to quarter and year. Please note that to specify the start of year, ' +
        'the start of week needs to be specified too.',
      examples: [
        "DATEPART('day', #2020-06-01#) = 1",
        "DATEPART('month', #2020-06-01#) = 6",
        "DATEPART('week', #2023-12-31#, 'Monday') = 52",
        `
          /*Default is SUNDAY*/
          DATEPART('week', #2023-12-31#) = 1
          DATEPART('quarter', #2023-12-31#, 'Sunday',’August’) = 2
        `,
        `
          /*Default is JANUARY*/
          DATEPART('quarter', #2023-12-31#) = 4
        `,
      ],
      category: ExprCategory.DateTime,
      type: ExprType.Function,
    },

    DATETRUNC: {
      name: 'DATETRUNC',
      sign: 'DATETRUNC(<datePart>, <datetime>, [<startOfWeek>, <startOfYear>])',
      desc:
        'Returns a date value equal to the given datetime expression truncated to the specified precision. ' +
        'The returned date value may change based on the day specified as start of the week or start of year. ' +
        'If not specified, the default for start of week is Sunday and the default start of year is January. ' +
        'Start of year option only applies to quarter and year. Please note that to specify the start of year, ' +
        'the start of week needs to be specified too.',
      examples: [
        "DATETRUNC('month', #2020-06-29#) = #2020-06-01#",
        "DATETRUNC('quarter', [Order Date])",
        "DATETRUNC('week', #2023-12-31#, 'Monday') = #2023-12-25#",
        `
          /*Default is SUNDAY*/
          DATETRUNC('week', #2023-12-31#) = #2023-12-31#
          DATETRUNC(('quarter', #2023-12-31#, 'Sunday', ’August’) = #2023-11-01#
        `,
        `
          /*Default is JANUARY*/
          DATETRUNC('quarter', #2023-12-31#) = #2023-10-01#
        `,
      ],
      category: ExprCategory.DateTime,
      type: ExprType.Function,
    },

    NOW: {
      name: 'NOW',
      sign: 'NOW()',
      desc: 'Returns the current datetime.',
      examples: ['NOW() = #2020-01-30 3:54:00 PM#'],
      category: ExprCategory.DateTime,
      type: ExprType.Function,
    },

    TODAY: {
      name: 'TODAY',
      sign: 'TODAY()',
      desc: 'Returns the current date.',
      examples: [`TODAY() = #${formatInUTC(now, 'yyyy-MM-dd')}#`],
      category: ExprCategory.DateTime,
      type: ExprType.Function,
    },

    /// Logical
    AND: {
      name: 'AND',
      sign: '<expression> AND <expression>',
      desc: 'Performs a logical junction on two expression.',
      examples: ['TRUE AND FALSE = FALSE', 'IF ZN([Order Purchase Time]) AND [Order Amount] > 0'],
      category: ExprCategory.Logical,
      type: ExprType.Statement,
    },

    OR: {
      name: 'OR',
      sign: '<expression> OR <expression>',
      desc: 'Performs a logical disjunction on two expression.',
      examples: ['TRUE OR FALSE = TRUE', 'IF ZN([Order Purchase Time]) OR [Order Amount] > 0'],
      category: ExprCategory.Logical,
      type: ExprType.Statement,
    },

    CASE: {
      name: 'CASE',
      sign: 'CASE <expression> WHEN <value> THEN <value> [... ELSE <value>] END',
      desc: 'Finds the first <value> that the <expression> matches and returns the corresponding value.',
      examples: ["CASE [Order Status] WHEN 'CANCELLED' THEN 0 ELSE 1 END"],
      category: ExprCategory.Logical,
      type: ExprType.Statement,
    },

    IF: {
      name: 'IF',
      sign: 'IF <expression> THEN <value> [ELSEIF <expression> THEN <value>] [ELSE <value>] END',
      desc: 'Tests a series of logical expressions and returns the corresponding <value> of the first expression that evaluates to TRUE.',
      examples: [
        `
          IF [Order Amount] - [Order Expense] > 0
          THEN 'Profitable'
          ELSE 'Nonprofitable'
          END
        `,
      ],
      category: ExprCategory.Logical,
      type: ExprType.Statement,
    },

    IFNULL: {
      name: 'IFNULL',
      sign: 'IFNULL(<expression>, <altExpression>)',
      desc:
        'Returns <expression> if it is not NULL, otherwise returns <altExpression>. ' +
        'If both inputs are NULL then NULL is returned.',
      examples: ['IFNULL(10, 5) = 10', 'IFNULL(NULL, 1) = 1'],
      category: ExprCategory.Logical,
      type: ExprType.Function,
    },

    ISNULL: {
      name: 'ISNULL',
      sign: 'ISNULL(<expression>)',
      desc: 'Returns TRUE if <expression> is NULL.',
      examples: ['ISNULL(NULL) = TRUE', 'ISNULL([Order Amount])'],
      category: ExprCategory.Logical,
      type: ExprType.Function,
    },

    /// Aggregate
    AVG: {
      name: 'AVG',
      sign: 'AVG(<expression>)',
      desc: 'Averages the values of items in a group, not including NULL values.',
      examples: ['AVG(1, 2, 3) = 2', 'AVG([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    COUNT: {
      name: 'COUNT',
      sign: 'COUNT(<expression>)',
      desc: 'Counts the number of items in a group, not including NULL values.',
      examples: ['COUNT(1, 1, 2, 3) = 4 ', 'COUNT([Order ID])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    COUNTD: {
      name: 'COUNTD',
      sign: 'COUNTD(<expression>)',
      desc: 'Counts the number of unique items in a group, not including NULL values.',
      examples: ['COUNTD(1, 1, 2, 3) = 3', 'COUNTD([Order ID])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    MAX: {
      name: 'MAX',
      sign: 'MAX(<expression>)',
      desc: 'Computes the item in a group with the largest value.',
      examples: ['MAX(1, 2, 3) = 3', 'MAX([Order ID])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    MEDIAN: {
      name: 'MEDIAN',
      sign: 'MEDIAN(<expression>)',
      desc: 'Computes the median of an expression, which is the value that the values in the expression are below 50% of the time.',
      examples: ['MEDIAN([1, 2, 3, 4, 5]) = 3', 'MEDIAN([1, 2, 3, 10]) = 2.5', 'MEDIAN([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    MIN: {
      name: 'MIN',
      sign: 'MIN(<expression>)',
      desc: 'Computes the item in a group with the smallest value.',
      examples: ['MIN(1, 2, 3) = 1', 'MIN([Order ID])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    SUM: {
      name: 'SUM',
      sign: 'SUM(<expression>)',
      desc: 'Sums over all values in the expression, not including NULL values.',
      examples: ['SUM(1, 2, 3) = 6', 'SUM([Order Amount]) = Revenue'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    PERCENTILE_1: {
      name: 'PERCENTILE_1',
      sign: 'PERCENTILE_1(<expression>)',
      desc: 'Computes the 1st percentile within an expression, which is the value that the values in the expression are below 1% of the time.',
      examples: ['PERCENTILE_1([1, 2, 3, 4, 5]) = 1.04', 'PERCENTILE_1([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    PERCENTILE_5: {
      name: 'PERCENTILE_5',
      sign: 'PERCENTILE_5(<expression>)',
      desc: 'Computes the 5th percentile within an expression, which is the value that the values in the expression are below 5% of the time.',
      examples: ['PERCENTILE_5([1, 2, 3, 4, 5]) = 1.2', 'PERCENTILE_5([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    PERCENTILE_25: {
      name: 'PERCENTILE_25',
      sign: 'PERCENTILE_25(<expression>)',
      desc: 'Computes the 25th percentile within an expression, which is the value that the values in the expression are below 25% of the time.',
      examples: ['PERCENTILE_25([1, 2, 3, 4, 5]) = 2', 'PERCENTILE_25([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    PERCENTILE_75: {
      name: 'PERCENTILE_75',
      sign: 'PERCENTILE_75(<expression>)',
      desc: 'Computes the 75th percentile within an expression, which is the value that the values in the expression are below 75% of the time.',
      examples: ['PERCENTILE_75([1, 2, 3, 4, 5]) = 4', 'PERCENTILE_75([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    PERCENTILE_95: {
      name: 'PERCENTILE_95',
      sign: 'PERCENTILE_95(<expression>)',
      desc: 'Computes the 95th percentile within an expression, which is the value that the values in the expression are below 95% of the time.',
      examples: ['PERCENTILE_95([1, 2, 3, 4, 5]) = 4.8', 'PERCENTILE_95([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    PERCENTILE_99: {
      name: 'PERCENTILE_99',
      sign: 'PERCENTILE_99(<expression>)',
      desc: 'Computes the 99th percentile within an expression, which is the value that the values in the expression are below 99% of the time.',
      examples: ['PERCENTILE_99([1, 2, 3, 4, 5]) = 4.96', 'PERCENTILE_99([Order Amount])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    STDEV: {
      name: 'STDEV',
      sign: 'STDEV(<expression>)',
      desc: `
        Returns the standard deviation of all values in the given expression based on a sample of the population.
      `,
      examples: ['STDEV([Order Quantity])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    STDEVP: {
      name: 'STDEVP',
      sign: 'STDEVP(<expression>)',
      desc: `
        Returns the standard deviation of all values in the given expression based on the entire population.
      `,
      examples: ['STDEVP([Order Quantity])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    VAR: {
      name: 'VAR',
      sign: 'VAR(<expression>)',
      desc: `
      	Returns the variance of all values in the given expression based on a sample of the population.
      `,
      examples: ['VAR([Order Quantity])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    VARP: {
      name: 'VARP',
      sign: 'VARP(<expression>)',
      desc: `
      	Returns the variance of all values in the given expression based on the entire population.
      `,
      examples: ['VARP([Order Quantity])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    SKEW: {
      name: 'SKEW',
      sign: 'SKEW(<expression>)',
      desc: `Returns the skewness of all input values.`,
      examples: ['SKEW([Order Quantity])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    KURT: {
      name: 'KURT',
      sign: 'KURT(<expression>)',
      desc: `Returns the excess kurtosis of all input values.`,
      examples: ['KURT([Order Quantity])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },
    MODE: {
      name: 'MODE',
      sign: 'MODE(<expression>)',
      desc: `Returns the most frequent value for the values within x. NULL values are ignored.`,
      examples: ['MODE([Order Quantity])'],
      category: ExprCategory.Aggregate,
      type: ExprType.Function,
    },

    /// Analytic
    FIRST: {
      name: 'FIRST',
      sign: 'FIRST()',
      desc: 'Returns the number of rows from the current row to the first row of the partition.',
      examples: [
        `
          /*Current row index is 2 of 5*/
          FIRST() = -1
        `,
      ],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    INDEX: {
      name: 'INDEX',
      sign: 'INDEX()',
      desc: 'Returns the index of the current row in the partition.',
      examples: [
        `
        /*Current row index is 2 of 5*/
        INDEX() = 2
      `,
      ],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    LAST: {
      name: 'LAST',
      sign: 'LAST()',
      desc: 'Returns the number of rows from the current row to the last row of the partition.',
      examples: [
        `
        /*Current row index is 2 of 5*/
        LAST() = 3
      `,
      ],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    LOOKUP: {
      name: 'LOOKUP',
      sign: 'LOOKUP(<expression>, [<offset>])',
      desc: 'Returns the value of the expression in a target row and can be specified as a relative offset number from the current row.',
      examples: [`LOOKUP(SUM([Order Quantity]), -1)`],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    TOTAL: {
      name: 'TOTAL',
      sign: 'TOTAL(<expression>)',
      desc: 'Returns the total for the given expression, calculated using all rows within that partition.',
      examples: [`TOTAL(SUM([order_amount]))`],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    RUNNING_AVG: {
      name: 'RUNNING_AVG',
      sign: 'RUNNING_AVG(<expression>)',
      desc: 'Returns the running average of the given expression, from the first row in the partition to the current row. The given expression must be either an aggregate or a constant.',
      examples: ['RUNNING_AVG(SUM([Order Amount]))'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    NTILE: {
      name: 'NTILE',
      sign: 'NTILE(<expression>, <number>, [<order>])',
      desc: 'Distributes the rows in an ordered partition into a specified number of groups. The groups are numbered, starting at one. For each row, NTILE returns the number of the group to which the row belongs.  Default order is descending.',
      examples: ['NTILE(SUM([Order Amount]), 4, "ASC")'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    RANK: {
      name: 'RANK',
      sign: 'RANK(<expression>, [<order>])',
      desc: 'Returns the rank of each row within the partition of a result set. The rank of a row is one plus the number of ranks that come before the row in question. Default order is descending.',
      examples: ['RANK(SUM([Order Amount]), "DESC")'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    RANK_DENSE: {
      name: 'RANK_DENSE',
      sign: 'RANK_DENSE(<expression>, [<order>])',
      desc: 'Returns the rank of each row within a result set partition, with no gaps in the ranking values. The rank of a specific row is one plus the number of distinct rank values that come before that specific row.  Default order is descending.',
      examples: ['RANK_DENSE(SUM([Order Amount]), "DESC")'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    RUNNING_COUNT: {
      name: 'RUNNING_COUNT',
      sign: 'RUNNING_COUNT(<expression>)',
      desc: 'Returns the running count of the given aggregate expression, from the first row in the partition to the current row. The given expression must be either an aggregate or a constant.',
      examples: ['RUNNING_COUNT(COUNT([Order Id]))'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    RUNNING_SUM: {
      name: 'RUNNING_SUM',
      sign: 'RUNNING_SUM(<expression>)',
      desc: `
        Computes the running sum of the given expression across groups,
        from the first row of the partition to the current row.
      `,
      examples: ['RUNNING_SUM(COUNT([Order ID]))', 'RUNNING_SUM(SUM([Order Quantity]))'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    WINDOW_AVG: {
      name: 'WINDOW_AVG',
      sign: 'WINDOW_AVG(<expression>, [<start>, <end>])',
      desc: 'Returns the average of the given expression within the window, where <start> and <end> are optional parameters that specify the window. The window is defined by means of offsets from the current row. The given expression must be either an aggregate or a constant.',
      examples: ['WINDOW_AVG(SUM([Order Amount]))', 'WINDOW_AVG(SUM([Order Amount]), -6, 0)'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    WINDOW_COUNT: {
      name: 'WINDOW_COUNT',
      sign: 'WINDOW_COUNT(<expression>, [<start>, <end>])',
      desc: 'Returns the count of the given expression within the window, where <start> and <end> are optional parameters that specify the window. The window is defined by means of offsets from the current row. The given expression must be either an aggregate or a constant.',
      examples: ['WINDOW_COUNT(COUNT([Order Id]))', 'WINDOW_COUNT(COUNT([Order Id]), -6, 0)'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    WINDOW_SUM: {
      name: 'WINDOW_SUM',
      sign: 'WINDOW_SUM(<expression>, [<start>, <end>])',
      desc: 'Returns the sum of the given expression within the window, where <start> and <end> are optional parameters that specify the window. The window is defined by means of offsets from the current row. The given expression must be either an aggregate or a constant.',
      examples: ['WINDOW_SUM(COUNT([Order ID]))', 'WINDOW_SUM(COUNT([Order ID]), -6, 0)'],
      category: ExprCategory.Analytic,
      type: ExprType.Function,
    },

    //Type conversions
    INT: {
      name: 'INT',
      sign: 'INT(<expression>)',
      desc: 'Convert the given expression to an integer. The results are rounded towards zero.',
      examples: [
        'INT(10.5) = 10',
        'INT("10.5") = 10',
        'INT(true) = 1',
        'INT(#2023-02-01T05:30:15.050Z#) = 1675229415050',
      ],
      category: ExprCategory.TypeConversion,
      type: ExprType.Function,
    },

    FLOAT: {
      name: 'FLOAT',
      sign: 'FLOAT(<expression>)',
      desc: 'Convert the given expression to a floating point number.',
      examples: [
        'FLOAT(10.5) = 10.5',
        'FLOAT("10.5") = 10.5',
        'FLOAT(true) = 1',
        'FLOAT(#2023-02-01T05:30:15.050Z#) = 1675229415050',
      ],
      category: ExprCategory.TypeConversion,
      type: ExprType.Function,
    },

    DATE: {
      name: 'DATE',
      sign: 'DATE(<expression>)',
      desc: 'Convert the given expression to YYYY-MM-DD date format. Returns NULL if expression cannot be converted to date.',
      examples: [
        'DATE(1672542245050) = #2023-01-01#',
        'DATE("2023-02-01T05:30:15.050Z") = #2023-02-01#',
        'DATE(#2023-02-01T05:30:15.050Z#) = #2023-02-01#',
      ],
      category: ExprCategory.TypeConversion,
      type: ExprType.Function,
    },

    DATETIME: {
      name: 'DATETIME',
      sign: 'DATETIME(<expression>)',
      desc: 'Convert the given expression to YYYY-MM-DD HH:MM:SS format. Returns NULL if expression cannot be converted to datetime.',
      examples: [
        'DATETIME(1672542245050) = #2023-01-01 03:04:05#',
        'DATETIME("2023-02-01T05:30:15.050Z") = #2023-02-01 05:30:15#',
        'DATETIME(#2023-02-01#) = #2023-02-01 00:00:00#',
      ],
      category: ExprCategory.TypeConversion,
      type: ExprType.Function,
    },
  };
}
